<template lang="pug">
    paginated-items(
        scope="base"
        type="account"
        :loading="loading"
        :items="accountsPaginated.items"
        :show-filters="showFilters"
        :filters="accountsPaginated.filters"
        :variables="accountsPaginated.variables"
        :all-columns="accountsPaginated.allColumns"
        :active-columns="accountsPaginated.activeColumns"
        :page-info="accountsPaginated.pageInfo"
        :sort="sort"
        @pagination-size-change="handleSizeChange"
        @pagination-current-change="handleCurrentChange"
        @show-table-columns="handleColsShow"
        @change-filter="handleFilterInputChange"
        @click="handleClick"
        @change-sort="handleSortInputChange"
    )
        div(slot="top")
            el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                .left
                    el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="addAccount")
                        | {{ $t('New Account') }}
                .right.hide-on-empty
                    el-button(
                        plain
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-filter fas"
                        @click="toggleFilterVisiblity"
                        :disabled="disable.showFilters || hasFilters"
                    )
                        span(v-if="showFilters") {{ $t('Hide filters') }}
                        span(v-else) {{ $t('Show filters') }}
        template(v-slot:name:prefix="{ item }")
            // check if is owner and add an icon
            el-tooltip(v-if="isOwner(item)" effect="dark" :content="$t('Owner')" placement="top")
                span.is-owner
                    i.el-icon-fa-asterisk.fas
        template(v-slot:actions="{ item }")
            el-button.mimic-button(
                v-if="item && user.id !== item.id && canMimic(item)"
                circle
                plain
                size="mini"
                type="info"
                icon="el-icon-fa-right-to-bracket fas"
                @click.stop.prevent="mimicUser(item)"
            )
</template>

<i18n>
{
    "de": {
        "New Account": "Neuer Account",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Owner": "Inhaber",
        "Account list could not be fetched.": "Benutzerliste konnte nicht abgerufen werden."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { canMimicUser } from '@/helpers/mimic';
import { adminClient as apolloClient } from '@/store/apollo';

import router from '@/router';
import pinia from '@/pinia';
import { useAuthStore, useSetupStore, useAccountStore, useTicketStore } from '@/stores';

import PaginatedItems from '@/components/PaginatedItems';

const minimumTextFilterLength = 2;
const textFilterKeys = [
    'company',
    'name',
    'first_name',
    'last_name',
    'email',
    'phone',
    'address_street',
    'address_city',
    'address_country',
];

export default {
    name: 'Accounts',
    data() {
        return {
            disable: {
                showFilters: false,
            },
        };
    },
    components: {
        // Tabs,
        PaginatedItems,
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.accountsPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.accountsPaginated.filters);
        },
        sort() {
            var variables = this.accountsPaginated.variables;
            var defaultSort = this.accountsPaginated.defaultSort || { field: 'id', direction: 'desc' };
            var order = variables.order !== undefined && variables.order !== '' ? variables.order : defaultSort.field;
            var direction =
                variables.direction !== undefined && variables.direction !== ''
                    ? variables.direction
                    : defaultSort.direction;

            return { field: order, direction: direction };
        },
        // tabs() {
        //     return [
        //         {
        //             name: this.$t('Accounts'),
        //             path: '/system/accounts',
        //             icon: {
        //                 name: 'user-tie',
        //                 group: 'fas'
        //             }
        //         },
        //         {
        //             name: this.$t('Websites'),
        //             path: '/system/abonements',
        //             icon: {
        //                 name: 'cubes',
        //                 group: 'fas'
        //             }
        //         },
        //     ]
        // },
        user() {
            return useAuthStore().user;
        },
        accountStore() {
            return useAccountStore();
        },
        loading() {
            return this.accountStore.loading;
        },
        accountsPaginated() {
            return this.accountStore.accountsPaginated;
        },
        setupStore() {
            return useSetupStore(pinia);
        },
    },
    methods: {
        // The same rule login-as enforces, so the button is never a dead end.
        canMimic(item) {
            return canMimicUser(item);
        },

        addAccount() {
            const params = { id: 'new' };
            router.push({ name: 'system-account-main', params });
        },
        handleClick(item) {
            const params = { id: item.id };
            router.push({ name: 'system-account-main', params });
        },
        handleSizeChange(itemsPerPage) {
            this.updateLoading(true);
            this.defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.updateLoading(true);
            this.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns);
            // apolloClient.cache.reset()
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineCurrentPage(1);
            this.defineFilterValue({ key, value });

            if (this.isShortTextFilter(key, value)) {
                this.debouncedRefetch.cancel();
                return;
            }

            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        isShortTextFilter(key, value) {
            const like = _.trim(_.get(value, 'like', ''));

            return textFilterKeys.includes(key) && like.length > 0 && like.length < minimumTextFilterLength;
        },
        handleSortInputChange(key, value) {
            this.defineSort({ key, value });
            this.refetch();
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters;

            let value = false;
            if (!visible) value = true;

            this.disable.showFilters = true;
            this.defineFilterVisibility(value);
            this.saveFilterVisibility({ scope: 'base', key: 'account', value }).finally(
                () => (this.disable.showFilters = false)
            );
        },
        isOwner(item) {
            return !!item.administrators.find((admin) => !!admin.owner);
        },
        isAdmin(item) {
            return item.administrators.length >= 1 && item.email_verified_at !== null;
        },
        refetch() {
            this.fetchAccountsPaginated()
                .then(() => this.updateLoading(false))
                .catch((err) => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                    this.updateLoading(false);

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Account list could not be fetched.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                });
        },
        mimicUser(item) {
            // routes through the support-ticket dialog (start/continue/none)
            return useTicketStore().requestMimic({
                userId: item.id,
                cmsOptions: (item.administrators || [])
                    .map((administrator) => administrator.cms)
                    .filter((cms) => cms && cms.id),
            });
        },
        fetchAccountsPaginated() {
            return this.accountStore.fetchAccountsPaginated();
        },
        updateLoading(value) {
            this.accountStore.updateLoading(value);
        },
        defineItemsPerPage(value) {
            this.accountStore.defineItemsPerPage(value);
        },
        defineCurrentPage(value) {
            this.accountStore.defineCurrentPage(value);
        },
        defineActiveColumns(value) {
            this.accountStore.defineActiveColumns(value);
        },
        defineFilterValue(payload) {
            this.accountStore.defineFilterValue(payload);
        },
        defineFilterVisibility(value) {
            this.accountStore.defineFilterVisibility(value);
        },
        defineSort(payload) {
            this.accountStore.defineSorting(payload);
        },
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
    },
    created() {
        // Assign in created() (not methods) so Vue 2's method wrapper doesn't
        // strip lodash's `.cancel` property off the debounced function.
        this.debouncedRefetch = _.debounce(() => {
            this.updateLoading(true);
            this.refetch();
        }, useSetupStore(pinia).debounce || 300);
    },
    mounted() {
        this.refetch();
    },
};
</script>

<style lang="sass" scoped>
.el-button--medium.is-circle
    padding: 5px

.mimic-button
    padding: 0
    width: 28px
    height: 28px
    font-size: 0.85rem
    &[disabled]
        pointer-events: none
    &:not([disabled]):hover
        background: $--color-primary
        border-color: $--color-primary
        color: white

.account-list
    :deep()
        .column.name
            position: relative
            .is-owner
                font-size: 0.6rem
                position: absolute
                color: $--color-primary
                top: 0
                left: -10px
                // i
                //     padding: 3px
                //     border-radius: 50%
                //     background: $ci-color-primary
</style>
